import React, { createContext, useContext, useReducer, useState } from 'react';

import type { TableMapDetails } from 'store/tableMapsStore/reducer';
import {
    clampAreaToMapSize,
    DEFAULT_AREA,
    DEFAULT_MAP_SIZE,
    EditorArea,
    Point,
    SizeMeters,
    TABLE_SHAPE_DEFAULTS,
    TableShapeSizes,
} from './mapGeometry';
import { MapFeature, MAX_FEATURES } from './features';
import { MapOpening, MAX_OPENINGS, splitOpeningsByOutline } from './openings';
import {
    EditHistory,
    EMPTY_HISTORY,
    recordEdit,
    redoEdit,
    RoomSnapshot,
    undoEdit,
} from './roomHistory';
import {
    applyTableInputs,
    parseTableSize,
    PendingTableEdit,
    tableChanged,
    tableEditOf,
    tableInputErrors,
    TableInputs,
    tableInputsOf,
} from './tableEdit';

export type TableShape = 'round' | 'square' | 'rectangle';

export interface EditorTable {
    id: string;
    label: string;
    shape: TableShape;
    seats: number;
    qrCode: string | null;
    x: number; // 0..1 center within the global rectangle
    y: number; // 0..1 center within the global rectangle
    width: number; // metres; 0 = legacy/unset, falls back to the per-shape default
    height: number; // metres; 0 = legacy/unset, falls back to the per-shape default
    rotation: number; // degrees clockwise, 0..359, about the table's own center
    isActive: boolean;
    isPositionLocked: boolean;
}

export interface TableMapDraft {
    name: string;
    isActive: boolean;
    gridEnabled: boolean;
    entrances: MapOpening[];
    features: MapFeature[];
    size: SizeMeters;
    area: EditorArea;
    /** Size given to newly placed tables; changing one resizes every table of that shape. Draft-only, never persisted. */
    tableDefaults: TableShapeSizes;
    /** False on a new map until a preset is picked or a drawing accepted. Draft-only, never persisted. */
    isShapeChosen: boolean;
    /** While true the wizard cannot leave the step or save. Draft-only. */
    isDrawingArea: boolean;
    hasPendingObjectEdit: boolean;
    tables: EditorTable[];
    pendingTable: PendingTableEdit | null;
    tableHistory: EditHistory<EditorTable[]>;
}

type WizardAction =
    | { type: 'SET_NAME'; payload: string }
    | { type: 'SET_IS_ACTIVE'; payload: boolean }
    | { type: 'SET_GRID_ENABLED'; payload: boolean }
    | { type: 'ADD_OPENING'; payload: MapOpening }
    | { type: 'SET_OPENING_LABEL'; payload: { id: string; label: string } }
    | { type: 'MOVE_OPENING'; payload: { id: string; position: Point } }
    | { type: 'REMOVE_OPENING'; payload: { id: string } }
    | { type: 'ADD_FEATURE'; payload: MapFeature }
    | { type: 'UPDATE_FEATURE'; payload: { id: string } & Partial<Omit<MapFeature, 'id'>> }
    | { type: 'REMOVE_FEATURE'; payload: { id: string } }
    | { type: 'RESTORE_ROOM_FEATURES'; payload: RoomSnapshot }
    | { type: 'SET_SIZE'; payload: SizeMeters }
    | { type: 'SET_AREA'; payload: Partial<EditorArea> }
    | { type: 'CHOOSE_AREA_SHAPE'; payload: Partial<EditorArea> }
    | { type: 'SET_DRAWING_AREA'; payload: boolean }
    | { type: 'SET_PENDING_OBJECT_EDIT'; payload: boolean }
    | { type: 'SET_TABLE_DEFAULT_SIZE'; payload: { shape: TableShape; size: SizeMeters } }
    | { type: 'ADD_TABLE'; payload: Omit<EditorTable, 'qrCode' | 'isActive' | 'isPositionLocked'> }
    | { type: 'UPDATE_TABLE_POSITION'; payload: { id: string; x: number; y: number } }
    | { type: 'REMOVE_TABLE'; payload: { id: string } }
    | { type: 'EDIT_PENDING_TABLE'; payload: { id: string; patch: Partial<TableInputs> } }
    | { type: 'APPLY_PENDING_TABLE' }
    | { type: 'DISCARD_PENDING_TABLE' }
    | { type: 'UNDO_TABLE_EDIT' }
    | { type: 'REDO_TABLE_EDIT' };

const RECORDED_TABLE_ACTIONS: WizardAction['type'][] = [
    'ADD_TABLE',
    'UPDATE_TABLE_POSITION',
    'REMOVE_TABLE',
    'APPLY_PENDING_TABLE',
];

interface WizardContextValue {
    draft: TableMapDraft;
    dispatch: React.Dispatch<WizardAction>;
    /** Canvas zoom shared by every step. View state: never part of the draft, its history or a save. */
    zoom: number;
    setZoom: (zoom: number) => void;
}

const defaultDraft: TableMapDraft = {
    name: '',
    isActive: true,
    gridEnabled: true,
    entrances: [],
    features: [],
    size: DEFAULT_MAP_SIZE,
    area: DEFAULT_AREA,
    tableDefaults: TABLE_SHAPE_DEFAULTS,
    isShapeChosen: false,
    isDrawingArea: false,
    hasPendingObjectEdit: false,
    tables: [],
    pendingTable: null,
    tableHistory: EMPTY_HISTORY,
};

/** Per-axis clamp to the map. `0` is the legacy/unset sentinel and `Math.min` leaves it at `0`. */
function fitToMapSize(size: SizeMeters, mapSize: SizeMeters): SizeMeters {
    return {
        width: Math.min(size.width, mapSize.width),
        height: Math.min(size.height, mapSize.height),
    };
}

/** The backend rejects a table dimension larger than the map's own. */
function clampTableDefaults(defaults: TableShapeSizes, mapSize: SizeMeters): TableShapeSizes {
    return {
        round: fitToMapSize(defaults.round, mapSize),
        square: fitToMapSize(defaults.square, mapSize),
        rectangle: fitToMapSize(defaults.rectangle, mapSize),
    };
}

export function hasUncommittedEdits(draft: TableMapDraft): boolean {
    return draft.isDrawingArea || draft.hasPendingObjectEdit;
}

export function isStepValid(stepIndex: number, draft: TableMapDraft): boolean {
    if (stepIndex === 0) {
        return draft.name.trim() !== '';
    }
    if (stepIndex === 1 || stepIndex === 2) {
        return !hasUncommittedEdits(draft);
    }
    if (stepIndex === 4) {
        return draft.tables.length > 0;
    }
    return true;
}

function chosenArea(state: TableMapDraft, shape: Partial<EditorArea>): EditorArea {
    return clampAreaToMapSize({ ...state.area, ...shape }, state.size);
}

export function openingsLostByShapeChoice(
    draft: TableMapDraft,
    shape: Partial<EditorArea>
): MapOpening[] {
    return splitOpeningsByOutline(draft.entrances, draft.size, draft.area, chosenArea(draft, shape))
        .removed;
}

export function canApplyPendingTable(draft: TableMapDraft): boolean {
    const pending = draft.pendingTable;
    if (!pending) return false;
    const { inputs } = pending;
    const otherTables = draft.tables.filter((t) => t.id !== pending.id);
    const errors = tableInputErrors(inputs, otherTables, draft.size);
    const isPartial =
        parseTableSize(inputs.width, draft.size.width).status === 'partial' ||
        parseTableSize(inputs.height, draft.size.height).status === 'partial';
    return !isPartial && Object.values(errors).every((error) => !error);
}

export function canSaveDraft(draft: TableMapDraft): boolean {
    return draft.name.trim() !== '' && !hasUncommittedEdits(draft);
}

/** A draft is stored inactive; the wizard's `isActive` is the state it gets on publish. */
export function draftFromDetails(details: TableMapDetails): TableMapDraft {
    return {
        name: details.name,
        isActive: details.isDraft || details.isActive,
        gridEnabled: details.gridEnabled,
        entrances: details.entrances,
        features: details.features,
        size: details.size,
        area: details.area,
        tableDefaults: details.tableDefaults,
        isShapeChosen: true,
        isDrawingArea: false,
        hasPendingObjectEdit: false,
        tables: details.tables,
        pendingTable: null,
        tableHistory: EMPTY_HISTORY,
    };
}

function editPendingTable(
    state: TableMapDraft,
    id: string,
    patch: Partial<TableInputs>
): TableMapDraft {
    const table = state.tables.find((t) => t.id === id);
    const current = state.pendingTable;
    if (!table || (current && current.id !== id)) return state;
    const base = current ?? { id, inputs: tableInputsOf(table), edit: tableEditOf(table) };
    const next = applyTableInputs(base, patch, state.tableDefaults, state.size);
    const untouched = tableInputsOf(table);
    const isUnchanged = (Object.keys(untouched) as (keyof TableInputs)[]).every(
        (key) => next.inputs[key] === untouched[key]
    );
    return { ...state, pendingTable: isUnchanged ? null : next };
}

/** An apply that changes nothing (e.g. only whitespace around the name) closes the edit without an undo step. */
function applyPendingTable(state: TableMapDraft): TableMapDraft {
    const pending = state.pendingTable;
    const table = state.tables.find((t) => t.id === pending?.id);
    if (!pending || !table || !canApplyPendingTable(state)) return state;
    const applied = { ...table, ...pending.edit };
    if (!tableChanged(applied, table)) return { ...state, pendingTable: null };
    return {
        ...state,
        pendingTable: null,
        tables: state.tables.map((t) => (t.id === pending.id ? applied : t)),
    };
}

function restoreTables(
    state: TableMapDraft,
    step: { history: EditHistory<EditorTable[]>; snapshot: EditorTable[] } | null
): TableMapDraft {
    if (!step || state.pendingTable) return state;
    return { ...state, tables: step.snapshot, tableHistory: step.history };
}

export function wizardReducer(state: TableMapDraft, action: WizardAction): TableMapDraft {
    const next = draftReducer(state, action);
    if (!RECORDED_TABLE_ACTIONS.includes(action.type) || next.tables === state.tables) {
        return next;
    }
    return { ...next, tableHistory: recordEdit(state.tableHistory, state.tables) };
}

function draftReducer(state: TableMapDraft, action: WizardAction): TableMapDraft {
    switch (action.type) {
        case 'SET_NAME':
            return { ...state, name: action.payload };
        case 'SET_IS_ACTIVE':
            return { ...state, isActive: action.payload };
        case 'SET_GRID_ENABLED':
            return { ...state, gridEnabled: action.payload };
        case 'ADD_OPENING':
            if (state.entrances.length >= MAX_OPENINGS) return state;
            return { ...state, entrances: [...state.entrances, action.payload] };
        case 'SET_OPENING_LABEL':
            return {
                ...state,
                entrances: state.entrances.map((o) =>
                    o.id === action.payload.id ? { ...o, label: action.payload.label } : o
                ),
            };
        case 'MOVE_OPENING':
            return {
                ...state,
                entrances: state.entrances.map((o) =>
                    o.id === action.payload.id ? { ...o, position: action.payload.position } : o
                ),
            };
        case 'REMOVE_OPENING':
            return {
                ...state,
                entrances: state.entrances.filter((o) => o.id !== action.payload.id),
            };
        case 'ADD_FEATURE':
            if (state.features.length >= MAX_FEATURES) return state;
            return { ...state, features: [...state.features, action.payload] };
        case 'UPDATE_FEATURE':
            return {
                ...state,
                features: state.features.map((f) =>
                    f.id === action.payload.id ? { ...f, ...action.payload } : f
                ),
            };
        case 'REMOVE_FEATURE':
            return {
                ...state,
                features: state.features.filter((f) => f.id !== action.payload.id),
            };
        case 'RESTORE_ROOM_FEATURES':
            return {
                ...state,
                entrances: action.payload.entrances,
                features: action.payload.features,
            };
        case 'SET_SIZE': {
            const size = action.payload;
            return {
                ...state,
                size,
                area: clampAreaToMapSize(state.area, size),
                tableDefaults: clampTableDefaults(state.tableDefaults, size),
                tables: state.tables.map((t) => ({ ...t, ...fitToMapSize(t, size) })),
                tableHistory: EMPTY_HISTORY,
            };
        }
        case 'SET_AREA': {
            const area = clampAreaToMapSize({ ...state.area, ...action.payload }, state.size);
            return { ...state, area };
        }
        case 'CHOOSE_AREA_SHAPE': {
            const area = chosenArea(state, action.payload);
            const { kept } = splitOpeningsByOutline(state.entrances, state.size, state.area, area);
            return { ...state, area, entrances: kept, isShapeChosen: true };
        }
        case 'SET_DRAWING_AREA':
            if (state.isDrawingArea === action.payload) return state;
            return { ...state, isDrawingArea: action.payload };
        case 'SET_PENDING_OBJECT_EDIT':
            if (state.hasPendingObjectEdit === action.payload) return state;
            return { ...state, hasPendingObjectEdit: action.payload };
        case 'SET_TABLE_DEFAULT_SIZE': {
            const { shape, size } = action.payload;
            return {
                ...state,
                tableDefaults: { ...state.tableDefaults, [shape]: size },
                tables: state.tables.map((t) =>
                    t.shape === shape ? { ...t, width: size.width, height: size.height } : t
                ),
                tableHistory: EMPTY_HISTORY,
            };
        }
        case 'ADD_TABLE':
            return {
                ...state,
                tables: [
                    ...state.tables,
                    { ...action.payload, qrCode: null, isActive: false, isPositionLocked: false },
                ],
            };
        case 'UPDATE_TABLE_POSITION': {
            const { id, x, y } = action.payload;
            if (state.tables.some((t) => t.id === id && t.isPositionLocked)) return state;
            return {
                ...state,
                tables: state.tables.map((t) => (t.id === id ? { ...t, x, y } : t)),
            };
        }
        case 'REMOVE_TABLE':
            return {
                ...state,
                tables: state.tables.filter((t) => t.id !== action.payload.id),
                pendingTable:
                    state.pendingTable?.id === action.payload.id ? null : state.pendingTable,
            };
        case 'EDIT_PENDING_TABLE':
            return editPendingTable(state, action.payload.id, action.payload.patch);
        case 'APPLY_PENDING_TABLE':
            return applyPendingTable(state);
        case 'DISCARD_PENDING_TABLE':
            return state.pendingTable ? { ...state, pendingTable: null } : state;
        case 'UNDO_TABLE_EDIT':
            return restoreTables(state, undoEdit(state.tableHistory, state.tables));
        case 'REDO_TABLE_EDIT':
            return restoreTables(state, redoEdit(state.tableHistory, state.tables));
        default:
            return state;
    }
}

const TableMapWizardContext = createContext<WizardContextValue | undefined>(undefined);

interface TableMapWizardProviderProps {
    children: React.ReactNode;
    initialDraft?: TableMapDraft;
}

export function TableMapWizardProvider({ children, initialDraft }: TableMapWizardProviderProps) {
    const [draft, dispatch] = useReducer(wizardReducer, initialDraft ?? defaultDraft);
    const [zoom, setZoom] = useState(1);
    return (
        <TableMapWizardContext.Provider value={{ draft, dispatch, zoom, setZoom }}>
            {children}
        </TableMapWizardContext.Provider>
    );
}

export function useTableMapWizard(): WizardContextValue {
    const ctx = useContext(TableMapWizardContext);
    if (!ctx) {
        throw new Error('useTableMapWizard must be used inside TableMapWizardProvider');
    }
    return ctx;
}

export type { WizardAction };
export type { EditorArea, AreaShapeKind, SizeMeters } from './mapGeometry';
